import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/src/types';
import { useRouter } from 'expo-router';
import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import theme from 'shared/styles/theme';

export interface EncourageDdAfterVaSuccessBottomSheetProps {
  bottomSheetRef?: RefObject<BottomSheetModalMethods | null>;
  onDismiss: () => void;
}

const EncourageDdAfterVaSuccessBottomSheet = ({
  bottomSheetRef,
  onDismiss,
}: EncourageDdAfterVaSuccessBottomSheetProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'getAdvanceSuccessStrings.encourageDdAfterVaSuccessBottomSheet',
  });
  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();
  const {
    handleDdManagementLinkTokenFailure,
    refetchDdManagementFlow,
    shouldLaunchPinwheelSDK,
    shouldRedirectToDDManagementPage,
  } = useDirectDepositManagementFlow();

  const closeBottomSheet = () => {
    bottomSheetRef?.current?.dismiss();
    onDismiss();
  };

  const handleDirectDeposit = () => {
    closeBottomSheet();

    if (shouldRedirectToDDManagementPage) {
      router.push('/move-money/direct-deposit/direct-deposit-management');
      return;
    }

    initiatePinwheel({
      onOutcome: shouldLaunchPinwheelSDK ? refetchDdManagementFlow : () => {},
      ...(shouldLaunchPinwheelSDK && {
        onLinkTokenError: handleDdManagementLinkTokenFailure,
      }),
    });
  };

  return (
    <BottomSheet
      ref={bottomSheetRef}
      analyticsId="encourageDdAfterVaSuccessViewed"
      onDismiss={onDismiss}
    >
      <View style={styles.contentContainer} testID="encourage-dd-bottom-sheet">
        <View style={styles.illustrationContainer}>
          <Illustration illustration="PAYCHECK_A" />
        </View>
        <TitleLgText numberOfLines={0} style={styles.titleText}>
          {translatedText('titleHigherFee')}
        </TitleLgText>
        <BodyBaseText numberOfLines={0} style={styles.bodyText}>
          {translatedText('body')}
        </BodyBaseText>
      </View>
      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('primaryButton')}
          onPress={handleDirectDeposit}
          isLoading={isPinwheelLoading}
          analyticsId="encourageDdAfterVaSuccessDirectDepositClicked"
          customAttributes={{ cta: 'Set up direct deposit' }}
        />
        <Button
          label={translatedText('secondaryButton')}
          onPress={closeBottomSheet}
          type="TEXT"
          analyticsId="encourageDdAfterVaSuccessDismissed"
          customAttributes={{ cta: 'Continue without direct deposit' }}
        />
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  bodyText: {
    textAlign: 'center',
  },
  buttonContainer: {
    gap: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  contentContainer: {
    alignItems: 'center',
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.l,
    paddingTop: theme.spacing.s,
  },
  illustrationContainer: {
    height: 72,
    width: 72,
  },
  titleText: {
    textAlign: 'center',
  },
});

export default EncourageDdAfterVaSuccessBottomSheet;
